import {
  Card,
  Col,
  Row,
  Empty,
  Typography,
  Tag,
  Divider,
  Space,
  Select,
} from "antd";
import { useMemo, useState } from "react";
import { BulbOutlined, EnvironmentOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { Option } = Select;

const ResearchCentresTab = ({ institution }) => {
  const { research_centres = [] } = institution;

  const [selectedArea, setSelectedArea] = useState("All");

  // Compute unique research area options
  const researchAreaOptions = useMemo(() => {
    const allAreas = research_centres.flatMap(
      (centre) => centre.research_areas || []
    );
    const uniqueAreas = Array.from(new Set(allAreas.map((a) => a.name)));
    return ["All", ...uniqueAreas];
  }, [research_centres]);

  // Filtered centres based on selected area
  const filteredCentres = useMemo(() => {
    if (selectedArea === "All") return research_centres;
    return research_centres.filter((centre) =>
      centre.research_areas?.some((area) => area.name === selectedArea)
    );
  }, [research_centres, selectedArea]);

  return (
    <div className="pt-5 pb-6">
      <div className="mb-6 flex flex-wrap gap-3">
        <Select
          value={selectedArea}
          onChange={setSelectedArea}
          className="min-w-[250px]"
        >
          {researchAreaOptions.map((area) => (
            <Option key={area} value={area}>
              {area}
            </Option>
          ))}
        </Select>
      </div>

      {filteredCentres.length === 0 ? (
        <Empty description="No research centres found." className="my-10" />
      ) : (
        <Row gutter={[24, 24]}>
          {filteredCentres.map((centre) => (
            <Col key={centre.id} xs={24} sm={12} md={6}>
              <Card
                title={
                  <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
                    <BulbOutlined className="text-blue-600" />
                    {centre.name}
                  </div>
                }
                className="transition-all duration-300 border border-gray-200! hover:shadow-sm"
              >
                <div className="text-sm text-gray-700 space-y-2">
                  {centre.location && (
                    <p>
                      <EnvironmentOutlined className="mr-2 text-gray-500" />
                      <strong>Location:</strong>{" "}
                      <span className="text-gray-800">{centre.location}</span>
                    </p>
                  )}

                  {centre.description && (
                    <>
                      <Divider className="my-2" />
                      <Text className="block text-gray-600 leading-relaxed">
                        {centre.description}
                      </Text>
                    </>
                  )}

                  {centre.research_areas?.length > 0 && (
                    <>
                      <Divider className="my-2" />
                      <Text className="font-medium text-sm text-gray-600">
                        Research Areas:
                      </Text>
                      &nbsp;
                      <Space wrap>
                        {centre.research_areas.map((area) => (
                          <Tag
                            key={area.id}
                            color="blue"
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {area.name}
                          </Tag>
                        ))}
                      </Space>
                    </>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default ResearchCentresTab;
